import React, { useCallback, useContext, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { MediaDropContextProvider } from '@components/MediaDropContext/MediaDropContext'
import { MessageTimelineContext } from '@components/MessageTimeline/MessageTimelineContext'
import { TownsEditorContainer } from '@components/RichTextPlate/TownsEditorContainer'
import { Attachment, ChannelMessageEvent } from '@towns-protocol/sdk'
import { Box, Stack, useZLayerContext } from '@ui'
import { useDevice } from 'hooks/useDevice'
import { useEditMessage } from 'hooks/useEditMessage'
import { useSpaceChannels } from 'hooks/useSpaceChannels'
import { SendTextMessageOptions, useMyProfile, useSpaceMembers } from 'use-towns-client'

import { useMessageEditContext } from './MessageEditContext'

type Props = {
    eventId: string
    latestEventId: string
    eventContent: ChannelMessageEvent
    channelId: string
    spaceId: string | undefined
    initialValue: string
    attachments?: Attachment[]
}

export const TimelineMessageEditor = (props: Props) => {
    const { isTouch } = useDevice()
    const { attachments, initialValue, channelId, spaceId, eventId, eventContent } = props
    const { timelineActions } = useContext(MessageTimelineContext) ?? {}
    const { editChannelMessage } = useEditMessage({ channelId, spaceId })

    const removedAttachmentIds = useMessageEditContext()?.removedAttachmentIds

    // Calculate the remaining attachment count after filtering out removed attachments
    const remainingAttachments = useMemo(
        () => attachments?.filter((a) => !removedAttachmentIds.includes(a.id)) || [],
        [attachments, removedAttachmentIds],
    )
    const remainingAttachmentCount = remainingAttachments.length

    const onSend = useCallback(
        (value: string, options: SendTextMessageOptions = {}) => {
            options.attachments = remainingAttachments

            editChannelMessage({ eventId, value }, eventContent, options)
            timelineActions?.onCancelEditingMessage?.()
        },
        [editChannelMessage, eventId, eventContent, timelineActions, remainingAttachments],
    )

    const onCancel = useCallback(() => {
        timelineActions?.onCancelEditingMessage?.()
    }, [timelineActions])

    const { memberIds } = useSpaceMembers()
    const userId = useMyProfile()?.userId
    const channels = useSpaceChannels()

    return isTouch ? (
        <TouchEditMessageWrapper onCancel={onCancel}>
            <Box grow />

            <TownsEditorContainer
                autoFocus
                editable
                editing
                displayButtons="always"
                initialValue={initialValue}
                channels={channels}
                spaceMemberIds={memberIds}
                userId={userId}
                remainingAttachmentCount={remainingAttachmentCount}
                onSend={onSend}
                onCancel={onCancel}
            />
        </TouchEditMessageWrapper>
    ) : (
        <Stack gap="xs">
            <MediaDropContextProvider
                channelId={channelId}
                spaceId={spaceId}
                title=""
                eventId={eventId}
            >
                <TownsEditorContainer
                    editable
                    editing
                    autoFocus
                    displayButtons="always"
                    initialValue={initialValue}
                    channels={channels}
                    spaceMemberIds={memberIds}
                    userId={userId}
                    remainingAttachmentCount={remainingAttachmentCount}
                    onSend={onSend}
                    onCancel={onCancel}
                />
            </MediaDropContextProvider>
        </Stack>
    )
}

export const TouchEditMessageWrapper = (props: {
    children: React.ReactNode
    onCancel: () => void
}) => {
    const { onCancel } = props
    const backgroundPressed = useCallback(
        (event: React.MouseEvent) => {
            event.preventDefault()
            event.stopPropagation()
            onCancel()
        },
        [onCancel],
    )

    const root = useZLayerContext().rootLayerRef?.current
    if (!root) {
        console.error(`no root context declared for use of modal`)
        return null
    }

    return createPortal(
        <Box zIndex="tooltips" pointerEvents="auto" paddingTop="x4">
            <Box absoluteFill onClick={backgroundPressed} />
            <Box position="absolute" bottom="none" right="none" left="none">
                {props.children}
            </Box>
        </Box>,
        // Attaching to root creates overlap & scroll issues on mobile due to height mismatch
        document.getElementById('editor-container') as Element,
    )
}
